import { useState, useCallback } from 'react'
import axios from 'axios'

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api'

export function useApi() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const request = useCallback(async (method, endpoint, data = null, config = {}) => {
    setLoading(true)
    setError(null)
    
    try {
      const url = `${API_URL}${endpoint}`
      const response = await axios({ method, url, data, ...config })
      return response.data
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'An error occurred'
      setError(message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const get = useCallback((endpoint, config) => request('GET', endpoint, null, config), [request])
  const post = useCallback((endpoint, data, config) => request('POST', endpoint, data, config), [request])
  const put = useCallback((endpoint, data, config) => request('PUT', endpoint, data, config), [request])
  const del = useCallback((endpoint, config) => request('DELETE', endpoint, null, config), [request])

  return { get, post, put, del, loading, error, setError }
}

// Specific API hooks
export function useDashboard() {
  const { get, loading, error } = useApi()
  
  const fetchDashboard = useCallback(() => get('/dashboard'), [get])
  
  return { fetchDashboard, loading, error }
}

export function useExpenses() {
  const { get, post, put, del, loading, error } = useApi()
  
  const fetchExpenses = useCallback((params = {}) => {
    const query = new URLSearchParams(params).toString()
    return get(`/expenses${query ? `?${query}` : ''}`)
  }, [get])
  
  const createExpense = useCallback((data) => post('/expenses', data), [post])
  const updateExpense = useCallback((id, data) => put(`/expenses/${id}`, data), [put])
  const deleteExpense = useCallback((id) => del(`/expenses/${id}`), [del])
  
  return { fetchExpenses, createExpense, updateExpense, deleteExpense, loading, error }
}

export function useLoads() {
  const { get, post, put, del, loading, error } = useApi()
  
  const fetchLoads = useCallback((params = {}) => {
    const query = new URLSearchParams(params).toString()
    return get(`/loads${query ? `?${query}` : ''}`)
  }, [get])
  
  const createLoad = useCallback((data) => post('/loads', data), [post])
  const updateLoad = useCallback((id, data) => put(`/loads/${id}`, data), [put])
  const deleteLoad = useCallback((id) => del(`/loads/${id}`), [del])
  
  return { fetchLoads, createLoad, updateLoad, deleteLoad, loading, error }
}

export function useCategories() {
  const { get, post, del, loading, error } = useApi()
  
  const fetchCategories = useCallback(() => get('/categories'), [get])
  const createCategory = useCallback((data) => post('/categories', data), [post])
  const deleteCategory = useCallback((id) => del(`/categories/${id}`), [del])
  
  return { fetchCategories, createCategory, deleteCategory, loading, error }
}

export function useRevenueCategories() {
  const { get, post, del, loading, error } = useApi()
  
  const fetchRevenueCategories = useCallback(() => get('/revenue-categories'), [get])
  const createRevenueCategory = useCallback((data) => post('/revenue-categories', data), [post])
  const deleteRevenueCategory = useCallback((id) => del(`/revenue-categories/${id}`), [del])
  
  return { fetchRevenueCategories, createRevenueCategory, deleteRevenueCategory, loading, error }
}

export function usePerDiem() {
  const { get, post, put, del, loading, error } = useApi()
  
  const fetchSettings = useCallback(() => get('/per-diem/settings'), [get])
  const updateSettings = useCallback((data) => put('/per-diem/settings', data), [put])
  const fetchDays = useCallback((year, month) => get(`/per-diem/days?year=${year}&month=${month}`), [get])
  const setDay = useCallback((data) => post('/per-diem/days', data), [post])
  const removeDay = useCallback((date) => del(`/per-diem/days/${date}`), [del])
  
  return { fetchSettings, updateSettings, fetchDays, setDay, removeDay, loading, error }
}

export function useOCR() {
  const { post, loading, error } = useApi()
  
  const processReceipt = useCallback((imageBase64) => 
    post('/ocr', { image_base64: imageBase64, document_type: 'receipt' }), [post])
  
  const processLoad = useCallback((imageBase64) => 
    post('/ocr', { image_base64: imageBase64, document_type: 'load' }), [post])
  
  const processEmailText = useCallback((emailText, documentType = 'load') => 
    post('/ocr', { email_text: emailText, document_type: documentType }), [post])
  
  return { processReceipt, processLoad, processEmailText, loading, error }
}

export function useReports() {
  const { get, loading, error } = useApi()
  
  const fetchSummary = useCallback((startDate, endDate) => {
    const params = new URLSearchParams()
    if (startDate) params.append('start_date', startDate)
    if (endDate) params.append('end_date', endDate)
    return get(`/reports/summary?${params.toString()}`)
  }, [get])
  
  const fetchPerDiemReport = useCallback((year) => 
    get(`/reports/per-diem?year=${year}`), [get])
  
  const fetchPnLReport = useCallback((startDate, endDate) => 
    get(`/reports/pnl?start_date=${startDate}&end_date=${endDate}`), [get])
  
  return { fetchSummary, fetchPerDiemReport, fetchPnLReport, loading, error }
}

export function useHousehold() {
  const { get, post, del, loading, error } = useApi()
  
  const fetchUsers = useCallback(() => get('/household/users'), [get])
  const createInvite = useCallback((email) => post('/household/invite', { email }), [post])
  const fetchInvites = useCallback(() => get('/household/invites'), [get])
  const deleteInvite = useCallback((id) => del(`/household/invites/${id}`), [del])
  const removeUser = useCallback((id) => del(`/household/users/${id}`), [del])
  
  return { fetchUsers, createInvite, fetchInvites, deleteInvite, removeUser, loading, error }
}

export function useMileage() {
  const { get, post, put, del, loading, error } = useApi()
  
  const fetchMileage = useCallback((params = {}) => {
    const query = new URLSearchParams(params).toString()
    return get(`/mileage${query ? `?${query}` : ''}`)
  }, [get])
  const fetchOpenEntry = useCallback(() => get('/mileage/open'), [get])
  const createEntry = useCallback((data) => post('/mileage', data), [post])
  const updateEntry = useCallback((id, data) => put(`/mileage/${id}`, data), [put])
  const deleteEntry = useCallback((id) => del(`/mileage/${id}`), [del])
  
  return { fetchMileage, fetchOpenEntry, createEntry, updateEntry, deleteEntry, loading, error }
}
